import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Auth, setToken } from '../services/api';

// Subscription tiers — matches UpgradeModal, PricingPage, LandingPage
export const SUBSCRIPTION_TIERS = {
  FREE: 'free',
  PLUS: 'plus',
  PRO: 'pro',
  ENTERPRISE: 'enterprise'
};

// Tier features — aligned with landing page pricing
export const TIER_FEATURES = {
  [SUBSCRIPTION_TIERS.FREE]: {
    name: 'Free',
    price: 0,
    features: [
      'All Congress trades (full history)',
      'All SEC insider filings',
      'Lobbying & gov contracts data',
      'Political donations (FEC)',
      'Paper trading & portfolio',
      '5 AI analyses per day',
      '15-min delayed prices',
    ],
    limits: {
      charts: 2,
      indicators: 5,
      watchlists: 3,
      watchlistSymbols: 10,
      alerts: 0,
      aiAnalyses: 5,
      tradeAlerts: 0,
      realtime: false,
    }
  },
  [SUBSCRIPTION_TIERS.PLUS]: {
    name: 'Plus',
    price: 29,
    features: [
      'Everything in Free',
      'Real-time market data',
      'Advanced filters on all datasets',
      '10 trade alerts per day',
      '10 AI analyses per day',
      'Stock screener',
      'Extended watchlists',
    ],
    limits: {
      charts: 4,
      indicators: 20,
      watchlists: 10,
      watchlistSymbols: 50,
      alerts: 10,
      aiAnalyses: 10,
      tradeAlerts: 10,
      realtime: true,
    }
  },
  [SUBSCRIPTION_TIERS.PRO]: {
    name: 'Pro',
    price: 49,
    features: [
      'Everything in Plus',
      'Full AI chat assistant',
      'Unlimited AI analyses',
      'Convergence signal detection',
      'Insider cluster buy alerts',
      'Lobbying → stock correlation',
      '50 AI trade alerts per day',
    ],
    limits: {
      charts: 10,
      indicators: 999,
      watchlists: 999,
      watchlistSymbols: 999,
      alerts: 50,
      aiAnalyses: 999,
      tradeAlerts: 50,
      realtime: true,
    }
  },
  [SUBSCRIPTION_TIERS.ENTERPRISE]: {
    name: 'Enterprise',
    price: 149,
    features: [
      'Everything in Pro',
      'Unlimited trade alerts',
      'Full REST API access',
      'CSV/JSON bulk exports',
      'Portfolio backtesting',
      'Priority support',
    ],
    limits: {
      charts: 999,
      indicators: 999,
      watchlists: 999,
      watchlistSymbols: 999,
      alerts: 999,
      aiAnalyses: 999,
      tradeAlerts: 999,
      realtime: true,
    }
  }
};

// Auth Context
const AuthContext = createContext(null);

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (Auth.isLoggedIn()) {
          const userData = await Auth.me();
          setUser(userData);
        }
      } catch (err) {
        console.error('Auth check failed:', err);
        Auth.logout();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Login function
  const login = useCallback(async (email, password, rememberMe = false) => {
    setError(null);
    setLoading(true);

    try {
      const userData = await Auth.login(email, password);
      setUser(userData);
      setLoading(false);
      return { success: true };
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  // Signup function
  const signup = useCallback(async (email, password, name) => {
    setError(null);
    setLoading(true);

    try {
      const userData = await Auth.signup(email, password, name);
      setUser(userData);
      setLoading(false);
      return { success: true };
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  // Logout function
  const logout = useCallback(() => {
    Auth.logout();
    setUser(null);
  }, []);

  // Update user profile
  const updateProfile = useCallback(async (updates) => {
    setError(null);
    try {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, [user]);

  // Change password
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    setError(null);
    try {
      // TODO: Add backend endpoint for password change
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  // Request password reset
  const requestPasswordReset = useCallback(async (email) => {
    setError(null);
    try {
      // TODO: Add backend endpoint for password reset
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  // Get user's current tier
  const getUserTier = useCallback(() => {
    return user?.subscription || SUBSCRIPTION_TIERS.FREE;
  }, [user]);

  // Check if user has feature access (using tier hierarchy)
  const hasFeature = useCallback((feature) => {
    if (!user) return false;
    const tier = user.subscription || SUBSCRIPTION_TIERS.FREE;
    const tierOrder = [SUBSCRIPTION_TIERS.FREE, SUBSCRIPTION_TIERS.PLUS, SUBSCRIPTION_TIERS.PRO, SUBSCRIPTION_TIERS.ENTERPRISE];
    const tierIndex = tierOrder.indexOf(tier);
    
    switch (feature) {
      case 'realtime':
      case 'advanced-filters':
      case 'screener':
      case 'alerts':
        return tierIndex >= 1; // Plus+
      case 'ai_chat':
      case 'unlimited_ai':
      case 'convergence':
      case 'insider_signals':
      case 'institutional_signals':
        return tierIndex >= 2; // Pro+
      case 'api_access':
      case 'data_exports':
      case 'backtesting':
        return tierIndex >= 3; // Enterprise
      default:
        return true;
    }
  }, [user]);

  // Get user's limits
  const getLimits = useCallback(() => {
    if (!user) return TIER_FEATURES[SUBSCRIPTION_TIERS.FREE].limits;
    const tier = user.subscription || SUBSCRIPTION_TIERS.FREE;
    return TIER_FEATURES[tier]?.limits || TIER_FEATURES[SUBSCRIPTION_TIERS.FREE].limits;
  }, [user]);

  // Tier checks
  const isPlus = (() => {
    const tier = user?.subscription || 'free';
    return ['plus', 'pro', 'enterprise'].includes(tier);
  })();

  const isPro = (() => {
    const tier = user?.subscription || 'free';
    return ['pro', 'enterprise'].includes(tier);
  })();

  const isEnterprise = user?.subscription === SUBSCRIPTION_TIERS.ENTERPRISE;

  const value = {
    user,
    loading,
    error,
    login,
    signup,
    logout,
    updateProfile,
    changePassword,
    requestPasswordReset,
    hasFeature,
    getLimits,
    getUserTier,
    isPlus,
    isPro,
    isEnterprise,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// HOC for protected routes
export const withAuth = (Component) => {
  return function ProtectedRoute(props) {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
      return (
        <div className="auth-loading">
          <div className="auth-loading-spinner" />
          <span>Loading...</span>
        </div>
      );
    }

    if (!isAuthenticated) {
      window.location.href = '/login';
      return null;
    }

    return <Component {...props} />;
  };
};

export default AuthContext;